import React, { useEffect, useMemo, useState } from 'react';
import { Save, X, FileText, Edit3, Sparkles, ChevronLeft, Mic, Loader2, AlertCircle } from 'lucide-react';
import {
    TreatmentNoteTemplate, Indication, SupportedLocale, TreatmentNote, AudioFile,
} from '../../../services/apiService';
import { pickLocale, localeFromPatient, SUPPORTED_LOCALES } from '../../../utils/i18n';
import { SectionInput } from './TemplateSectionInputs';
import { renderTemplate } from './templateRenderer';
import AudioRecorder from './AudioRecorder';
import ProcedureSidebar from './ProcedureSidebar';

const labelClass = 'text-[11px] font-medium uppercase tracking-wider text-brand-text-muted';
const headerInputClass =
    'text-base font-semibold bg-transparent border-0 border-b border-transparent hover:border-brand-border focus:border-brand-primary focus:outline-none px-1 py-0.5 transition-colors text-brand-text min-w-[200px]';

export interface TemplateEditorSavePayload {
    template_id: number;
    section_values: Record<string, any>;
    body_overridden: boolean;
    body?: string;
    locale: SupportedLocale;
    title?: string;
}

export interface TemplateEditorAudioHandlers {
    /** Audio-Aufnahme darf nur starten wenn true. */
    consentGranted: boolean;
    /** Audio-Files dieser Note (Edit-Mode) inkl. transcript_status. */
    existingAudios?: AudioFile[];
    /** Optional: Reload-Hook fuer Polling (gibt aktualisierte Liste zurueck). */
    reloadAudios?: () => Promise<AudioFile[]>;
    /** Wird mit dem frisch erstellten Blob aufgerufen — Parent macht den eigentlichen Upload. */
    onUpload?: (noteId: number, blob: Blob, durationSec: number, locale: SupportedLocale) => Promise<AudioFile | null>;
}

interface Props {
    template: TreatmentNoteTemplate;
    indications: Indication[];
    practitionerName: string;
    patientName: string;
    patientPreferredLocale?: string | null;
    /** Bestehende Note (Edit-Modus) — undefined fuer neue Note */
    existingNote?: TreatmentNote | null;
    onSave: (payload: TemplateEditorSavePayload) => Promise<TreatmentNote | null | void>;
    onCancel: () => void;
    submitting?: boolean;
    audio?: TemplateEditorAudioHandlers;
}

const TreatmentNoteTemplateEditor: React.FC<Props> = ({
    template, indications, practitionerName, patientName,
    patientPreferredLocale, existingNote, onSave, onCancel, submitting = false, audio,
}) => {
    const defaultLocale = existingNote
        ? localeFromPatient(patientPreferredLocale)
        : localeFromPatient(patientPreferredLocale || template.language_default);

    const [locale, setLocale] = useState<SupportedLocale>(defaultLocale);
    const [sectionValues, setSectionValues] = useState<Record<string, any>>(
        existingNote?.section_values || {},
    );
    const [bodyOverridden, setBodyOverridden] = useState<boolean>(
        existingNote?.body_overridden === true,
    );
    const [manualBody, setManualBody] = useState<string>(
        existingNote?.body_overridden ? (existingNote?.body || '') : '',
    );
    const [title, setTitle] = useState<string>(
        pickLocale(template.name, defaultLocale),
    );

    // Title beim Locale-Wechsel angleichen, sofern noch ungeaendert vom Template-Default.
    useEffect(() => {
        const defaultTitleForLocale = pickLocale(template.name, locale);
        setTitle(prev => {
            const previousDefault = pickLocale(template.name, locale === 'de' ? 'es' : 'de');
            if (prev === previousDefault || prev === '') return defaultTitleForLocale;
            return prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locale, template.id]);

    const renderedBody = useMemo(() => renderTemplate(
        template,
        sectionValues,
        locale,
        { title, practitioner_name: practitionerName, patient_name: patientName },
        indications,
    ), [template, sectionValues, locale, title, practitionerName, patientName, indications]);

    const handleSectionChange = (key: string) => (next: any) => {
        setSectionValues(prev => ({ ...prev, [key]: next }));
    };

    const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
    const [recordedDuration, setRecordedDuration] = useState(0);
    const [audios, setAudios] = useState<AudioFile[]>(audio?.existingAudios || []);
    const [uploadingAudio, setUploadingAudio] = useState(false);

    useEffect(() => {
        setAudios(audio?.existingAudios || []);
    }, [audio?.existingAudios]);

    // Transcript-Polling: solange irgendein Audio dieser Note status='pending'
    // ist, alle 3s reloaden, max 60s.
    useEffect(() => {
        if (!audio?.reloadAudios) return;
        const hasPending = audios.some(a => a.transcript_status === 'pending');
        if (!hasPending) return;
        const started = Date.now();
        const id = window.setInterval(async () => {
            if (Date.now() - started > 60_000) {
                window.clearInterval(id);
                return;
            }
            try {
                const next = await audio.reloadAudios!();
                setAudios(next);
                if (!next.some(a => a.transcript_status === 'pending')) {
                    window.clearInterval(id);
                }
            } catch {
                /* still polling */
            }
        }, 3000);
        return () => window.clearInterval(id);
    }, [audios, audio?.reloadAudios]);

    const handleSave = async () => {
        const payload: TemplateEditorSavePayload = {
            template_id: template.id,
            section_values: sectionValues,
            body_overridden: bodyOverridden,
            body: bodyOverridden ? manualBody : undefined,
            locale,
            title,
        };
        const saved = await onSave(payload);
        // Wenn neue Note + Audio aufgenommen: jetzt hochladen.
        if (saved && typeof saved === 'object' && 'id' in saved && recordedBlob && audio?.onUpload) {
            setUploadingAudio(true);
            try {
                const af = await audio.onUpload(saved.id, recordedBlob, recordedDuration, locale);
                if (af) setAudios(prev => [af, ...prev]);
                setRecordedBlob(null);
                setRecordedDuration(0);
            } finally {
                setUploadingAudio(false);
            }
        }
        return saved;
    };

    const handleAppendTranscript = (text: string) => {
        if (!text) return;
        if (bodyOverridden) {
            setManualBody(prev => (prev ? prev + '\n\n' + text : text));
        } else {
            // Section "transcript" wird in einer impliziten Freitext-Section
            // angehaengt — falls Template eine text_freeform-Section hat,
            // suchen wir die und befuellen sie. Sonst geht's in den Body via
            // body_overridden + manueller Edit.
            const freetextSection = template.sections.find(s => s.type === 'text_freeform');
            if (freetextSection) {
                setSectionValues(prev => ({
                    ...prev,
                    [freetextSection.key]: prev[freetextSection.key]
                        ? `${prev[freetextSection.key]}\n${text}`
                        : text,
                }));
            } else {
                setBodyOverridden(true);
                setManualBody(prev => (prev ? prev + '\n\n' + text : text));
            }
        }
    };

    return (
        <div className="flex flex-col h-full bg-brand-bg">
            {/* Header */}
            <header className="shrink-0 h-14 flex items-center justify-between px-6 border-b border-brand-border bg-brand-surface">
                <div className="flex items-center gap-3 min-w-0">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="p-1.5 rounded-md text-brand-text-muted hover:bg-brand-surface-2 hover:text-brand-text cursor-pointer transition-colors"
                        aria-label="Abbrechen"
                        title="Abbrechen"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <FileText className="w-5 h-5 text-brand-text-muted shrink-0" />
                    <input
                        type="text"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        className={headerInputClass}
                        aria-label="Titel der Dokumentation"
                    />
                    <span className="text-[11px] text-brand-text-muted whitespace-nowrap">
                        Vorlage: {template.slug} · v{template.version}
                    </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-brand-text-muted whitespace-nowrap">
                        Behandler: <span className="text-brand-text font-medium">{practitionerName}</span>
                    </span>
                    <span className="text-xs text-brand-text-muted whitespace-nowrap">
                        Patient: <span className="text-brand-text font-medium">{patientName}</span>
                    </span>
                    <select
                        value={locale}
                        onChange={e => setLocale(e.target.value as SupportedLocale)}
                        className="text-xs px-2 py-1 border border-brand-border bg-brand-surface text-brand-text rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary/40 cursor-pointer"
                        aria-label="Sprache"
                    >
                        {SUPPORTED_LOCALES.map(l => (
                            <option key={l} value={l}>{l.toUpperCase()}</option>
                        ))}
                    </select>
                    {audio && (
                        <div className="pl-3 ml-1 border-l border-brand-border">
                            <AudioRecorder
                                disabled={!audio.consentGranted}
                                disabledReason={!audio.consentGranted ? 'Patient hat keine Audio-Einwilligung erteilt' : undefined}
                                onRecordingChange={(blob, dur) => {
                                    setRecordedBlob(blob);
                                    setRecordedDuration(dur);
                                }}
                            />
                        </div>
                    )}
                    <label className="flex items-center gap-1.5 text-xs text-brand-text-muted cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={bodyOverridden}
                            onChange={e => setBodyOverridden(e.target.checked)}
                            className="rounded border-brand-border cursor-pointer"
                        />
                        <Edit3 className="w-3.5 h-3.5" aria-hidden />
                        Manueller Edit
                    </label>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-3 py-1.5 text-sm text-brand-text-muted hover:text-brand-text cursor-pointer transition-colors"
                    >
                        Abbrechen
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={submitting}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-brand-primary text-white text-sm font-medium rounded-md hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    >
                        <Save className="w-4 h-4" aria-hidden />
                        {submitting ? 'Speichert ...' : 'Speichern'}
                    </button>
                </div>
            </header>

            {/* Body */}
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-[2fr_2fr_1fr] gap-4 p-6 overflow-hidden">
                {/* Left: Section-Inputs */}
                <div className="overflow-y-auto pr-1 space-y-4 -mr-1">
                    {(template.sections || []).map(section => (
                        <SectionInput
                            key={section.key}
                            section={section}
                            value={sectionValues[section.key]}
                            onChange={handleSectionChange(section.key)}
                            locale={locale}
                            indications={indications}
                        />
                    ))}
                    {(template.sections || []).length === 0 && (
                        <div className="rounded-xl bg-brand-surface border border-dashed border-brand-border p-8 text-center">
                            <p className="text-sm text-brand-text-muted">
                                Diese Vorlage hat keine Sektionen.
                            </p>
                        </div>
                    )}
                </div>

                {/* Right: Live-Doku oder manueller Editor + Audio/Transcript */}
                <aside className="rounded-xl bg-brand-surface border border-brand-border shadow-sm flex flex-col overflow-hidden">
                    <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-brand-border">
                        <h3 className={labelClass}>Dokumentation</h3>
                        <span className="inline-flex items-center gap-1 text-[10px] text-brand-text-muted">
                            {bodyOverridden ? (
                                <><Edit3 className="w-3 h-3" aria-hidden /> Manuell</>
                            ) : (
                                <><Sparkles className="w-3 h-3" aria-hidden /> Auto-generiert</>
                            )}
                        </span>
                    </div>
                    {bodyOverridden ? (
                        <textarea
                            value={manualBody}
                            onChange={e => setManualBody(e.target.value)}
                            placeholder="Manuell editierter Dokumentations-Text — überschreibt das Template-Rendering."
                            className="flex-1 w-full px-5 py-4 text-sm leading-relaxed text-brand-text bg-brand-surface placeholder:text-brand-text-muted border-0 focus:outline-none resize-none font-sans"
                        />
                    ) : (
                        <pre className="flex-1 overflow-y-auto px-5 py-4 text-sm leading-relaxed text-brand-text whitespace-pre-wrap font-sans">
                            {renderedBody || (
                                <span className="text-brand-text-muted italic">
                                    Noch keine Sektion ausgefüllt — Doku-Vorschau erscheint hier, sobald du links Werte wählst.
                                </span>
                            )}
                        </pre>
                    )}
                    {(audios.length > 0 || uploadingAudio) && (
                        <div className="shrink-0 border-t border-brand-border bg-brand-bg/30 max-h-56 overflow-y-auto">
                            <div className="px-5 py-2 border-b border-brand-border flex items-center justify-between">
                                <h4 className={labelClass}>Audio &amp; Transkript</h4>
                                <span className="text-[10px] text-brand-text-muted">{audios.length} Aufnahme(n)</span>
                            </div>
                            {uploadingAudio && (
                                <div className="flex items-center gap-2 px-5 py-2 text-xs text-brand-text-muted">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Lade Audio hoch ...
                                </div>
                            )}
                            <ul className="divide-y divide-brand-border">
                                {audios.map(a => (
                                    <li key={a.id} className="px-5 py-3 text-xs">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="inline-flex items-center gap-1.5">
                                                <Mic className="w-3.5 h-3.5 text-brand-text-muted" />
                                                <span className="text-brand-text">
                                                    {a.duration_sec ? `${a.duration_sec}s` : 'Aufnahme'}
                                                </span>
                                                <span className="text-brand-text-muted">·</span>
                                                {a.transcript_status === 'pending' && (
                                                    <span className="inline-flex items-center gap-1 text-amber-700">
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                        Transkribiert ...
                                                    </span>
                                                )}
                                                {a.transcript_status === 'done' && (
                                                    <span className="inline-flex items-center gap-1 text-green-700">
                                                        <Sparkles className="w-3 h-3" />
                                                        Fertig
                                                    </span>
                                                )}
                                                {a.transcript_status === 'failed' && (
                                                    <span className="inline-flex items-center gap-1 text-red-700" title={a.transcript_error || undefined}>
                                                        <AlertCircle className="w-3 h-3" />
                                                        Fehler
                                                    </span>
                                                )}
                                                {a.transcript_status === 'skipped' && (
                                                    <span className="text-brand-text-muted" title={a.transcript_error || 'Kein API-Key konfiguriert'}>
                                                        Übersprungen (kein STT-Key)
                                                    </span>
                                                )}
                                            </span>
                                            {a.transcript_status === 'done' && a.transcript_text && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleAppendTranscript(a.transcript_text)}
                                                    className="px-2 py-0.5 text-[11px] font-medium text-brand-primary hover:bg-brand-primary/10 rounded cursor-pointer transition-colors"
                                                >
                                                    In Doku übernehmen
                                                </button>
                                            )}
                                        </div>
                                        {a.transcript_status === 'done' && a.transcript_text && (
                                            <p className="text-brand-text whitespace-pre-wrap leading-relaxed">{a.transcript_text}</p>
                                        )}
                                        {a.transcript_status === 'failed' && a.transcript_error && (
                                            <p className="text-red-700 text-[11px]">{a.transcript_error}</p>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </aside>

                {/* Procedure-Sidebar (Phase D) */}
                <ProcedureSidebar
                    template={template}
                    sectionValues={sectionValues}
                    locale={locale}
                />
            </div>
        </div>
    );
};

export default TreatmentNoteTemplateEditor;
